import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, Loader2, X } from 'lucide-react';
import './Toast.css';

// Generic messaging component (Issue #194). One component for every in-app
// notice, so the ad-hoc toasts (StatusToast / UpdatePrompt / paFlash) can
// converge on a single look + behavior.
//
// Alex's spec:
//   • slides in from the RIGHT edge with a fade
//   • three colors: info=blue, error=red, success=green
//   • success auto-hides (fades itself out); error + info wait for an X click
//   • …but that dismiss behavior is GENERICALLY configurable per instance —
//     any variant can be made auto-hiding, persistent, or manual-dismiss.

// info=blue (notice/action), error=red, success=green (done), pending=amber/yellow
// (an intermediate "in progress" step, e.g. "Sende an Prompt Agent…", #201).
export type ToastVariant = 'info' | 'error' | 'success' | 'pending';
// 'auto'   → fades out on its own after `autoHideMs`
// 'manual' → stays until the user clicks ✕
// 'persist'→ stays forever, no ✕ (caller controls removal)
export type ToastDismiss = 'auto' | 'manual' | 'persist';

export interface ToastProps {
  variant?: ToastVariant;          // default 'info'
  /** Override the per-variant default (success→auto, info/error→manual). */
  dismiss?: ToastDismiss;
  /** Auto-hide delay in ms (only for dismiss='auto'). Default 5000. */
  autoHideMs?: number;
  title?: ReactNode;
  children?: ReactNode;
  /** Replace the default variant icon (pass null to hide it). */
  icon?: ReactNode | null;
  /** Called after the leave animation once the toast is gone. */
  onDismiss?: () => void;
  /** Click anywhere on the card (e.g. "open details"). */
  onClick?: () => void;
}

const DEFAULT_ICON: Record<ToastVariant, ReactNode> = {
  info: <Info size={18} />,
  error: <AlertTriangle size={18} />,
  success: <CheckCircle2 size={18} />,
  pending: <Loader2 size={18} />,
};

// success auto-hides; the attention-worthy ones (info/error) wait for a click.
function defaultDismiss(variant: ToastVariant): ToastDismiss {
  return variant === 'success' ? 'auto' : 'manual';
}

const LEAVE_MS = 240; // keep in sync with the toast-out animation in Toast.css

export function Toast({
  variant = 'info',
  dismiss,
  autoHideMs = 5000,
  title,
  children,
  icon,
  onDismiss,
  onClick,
}: ToastProps) {
  const mode = dismiss ?? defaultDismiss(variant);
  const [leaving, setLeaving] = useState(false);

  // Keep onDismiss in a ref so `close` is STABLE. Bug #200: GlobalToasts passes a
  // fresh `onDismiss` arrow on every store update, so a close() keyed on onDismiss
  // changed identity each render → the auto-hide effect re-ran → its setTimeout was
  // cleared + restarted every render → an auto toast NEVER fired and stayed forever.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Play the leave animation, THEN notify the parent to unmount us.
  const close = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onDismissRef.current?.(), LEAVE_MS);
  }, []);

  useEffect(() => {
    if (mode !== 'auto') return;
    const t = setTimeout(close, autoHideMs);
    return () => clearTimeout(t);
  }, [mode, autoHideMs, close]);

  const shownIcon = icon === undefined ? DEFAULT_ICON[variant] : icon;

  return (
    <div
      className={`toast toast-${variant}${leaving ? ' toast-leaving' : ''}${onClick ? ' toast-clickable' : ''}`}
      role={variant === 'error' ? 'alert' : 'status'}
      onClick={onClick}
    >
      {shownIcon && <span className="toast-icon">{shownIcon}</span>}
      <div className="toast-body">
        {title && <div className="toast-title">{title}</div>}
        {children && <div className="toast-msg">{children}</div>}
      </div>
      {mode === 'manual' && (
        <button
          type="button"
          className="toast-x"
          aria-label="Schließen"
          onClick={(e) => { e.stopPropagation(); close(); }}
        >
          <X size={15} />
        </button>
      )}
      {/* Countdown bar for auto-hide toasts (#202) — depletes over autoHideMs so you
          see how long the message will stay. */}
      {mode === 'auto' && (
        <span className="toast-timer" style={{ animationDuration: `${autoHideMs}ms` }} />
      )}
    </div>
  );
}

// Fixed positioning container: top-right on desktop (slides in from the right
// edge, Issue #189), centered on mobile where a right-edge card would crowd the
// screen. Stack multiple toasts vertically.
export function ToastStack({ children }: { children: ReactNode }) {
  return <div className="toast-stack">{children}</div>;
}
